import axios from 'axios';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { Message, Modal } from '@arco-design/web-vue';
import { useUserStore } from '@/store';
import { getToken } from '@/utils/auth';

export interface HttpResponse<T = unknown> {
  status: number;
  msg: string;
  code: number;
  data: T;
}

if (import.meta.env.VITE_API_BASE_URL) {
  axios.defaults.baseURL = import.meta.env.VITE_API_BASE_URL;
}

axios.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    // let each request carry token
    // this example using the JWT token
    // Authorization is a custom headers key
    // please modify it according to the actual situation
    const token = getToken();
    if (token) {
      if (!config.headers) {
        config.headers = {};
      }
      config.headers.Authorization = `${token}`;
    }
    return config;
  },
  (error) => {
    // do something
    return Promise.reject(error);
  }
);
// add response interceptors
axios.interceptors.response.use(
  (response: AxiosResponse<HttpResponse>) => {
    const res = response.data;
    // if the custom code is not 0, it is judged as an error.
    if (res && res.code !== 0) {
      Message.error({
        content: res.msg || 'Error',
        duration: 5 * 1000,
      });
      // 50008: Illegal token; 50012: Other clients logged in; 50014: Token expired;
      // if (
      //   [50008, 50012, 50014].includes(res.code) &&
      //   response.config.url !== '/api/user/info'
      // ) {
      //   Modal.error({
      //     title: 'Confirm logout',
      //     content:
      //       'You have been logged out, you can cancel to stay on this page, or log in again',
      //     okText: 'Re-Login',
      //     async onOk() {
      //       const userStore = useUserStore();

      //       await userStore.logout();
      //       window.location.reload();
      //     },
      //   });
      // }
      return Promise.reject(new Error(res.msg || 'Error'));
    }
    return res;
  },
  (error) => {
    // 根据不同的错误类型显示友好的提示
    let errorMessage = '请求失败，请稍后重试';

    if (error.response) {
      // 服务器返回了错误响应
      const { status, data } = error.response;

      switch (status) {
        case 400:
          errorMessage = data?.msg || '请求参数错误';
          break;
        case 401:
          errorMessage = '登录已过期，请重新登录';
          break;
        case 403:
          errorMessage = '没有权限访问该资源';
          break;
        case 404:
          errorMessage = '请求的资源不存在';
          break;
        case 500:
          errorMessage = data?.msg || '服务器内部错误，请稍后重试';
          break;
        case 502:
          errorMessage = '网关错误，服务暂时不可用';
          break;
        case 503:
          errorMessage = '服务维护中，请稍后再试';
          break;
        case 504:
          errorMessage = '请求超时，请检查网络连接';
          break;
        default:
          errorMessage = data?.msg || `请求失败 (${status})`;
      }
    } else if (error.request) {
      // 请求已发出，但没有收到响应（网络断开、服务器无响应等）
      if (error.code === 'ECONNABORTED') {
        errorMessage = '请求超时，请检查网络连接';
      } else if (error.message === 'Network Error') {
        errorMessage = '网络连接失败，请检查网络设置';
      } else {
        errorMessage = '服务器无响应，请稍后重试';
      }
    } else {
      // 请求配置出错
      errorMessage = error.message || '请求配置错误';
    }

    Message.error({
      content: errorMessage,
      duration: 5 * 1000,
    });
    return Promise.reject(error);
  }
);
